import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useDashboardPermissions, usePlatformFeatureFlags } from '@components';
import { Button, Toasts } from '@fourthwall/components';
import { useShopStage } from '@hooks';
import { useGetCampaignQuery } from '../../../../../hooks/useGetCampaignQuery';
import { useRecommendationsDismiss } from '../../../hooks/useRecommendationsDismiss';
import { AppsIncentive } from '../components/AppsIncentive';
import { Campaign } from '../components/Campaign';
import { EnrollBanner } from '../components/Campaign/EnrollBanner';
import { DiscordIncentive } from '../components/DiscordIncentive';
import { DoubleReferralBonusIncentive } from '../components/DoubleReferralBonusIncentive';
import { MakeYourFirstSalesIncentive } from '../components/MakeYourFirstSalesIncentive';
import { MobileAppIncentive } from '../components/MobileAppIncentive';
import { ProductsIncentive } from '../components/ProductsIncentive';
import { PromotionIncentive } from '../components/PromotionIncentive';
import { SamplesIncentive } from '../components/SamplesIncentive';
import { useEnrollCampaignMutation } from '../queries/useEnrollCampaignMutation';
function nonNullable(value) {
    return Boolean(value);
}
export const useCampaignRecommendation = () => {
    const { isLoading: isLoadingShopStage } = useShopStage();
    const { hasFeatureFlag } = usePlatformFeatureFlags();
    const { hasPermission } = useDashboardPermissions();
    const { recommendationsDismiss, createOnDismiss } = useRecommendationsDismiss();
    const { campaignQuery } = useGetCampaignQuery(undefined, {
        enabled: hasPermission('home.campaign'),
    });
    const { enrollCampaignMutation } = useEnrollCampaignMutation();
    useEffect(() => {
        if (!campaignQuery.data?.onboardingCampaignId)
            return;
        TagManager.dataLayer({
            dataLayer: {
                onboardingCampaignId: campaignQuery.data?.onboardingCampaignId,
            },
        });
    }, [campaignQuery.data?.onboardingCampaignId]);
    const campaign = campaignQuery.data;
    const visible = recommendationsDismiss.campaign.visible && campaign;
    const getIncentive = (incentive) => {
        switch (incentive.type) {
            case 'VERIFY_DISCORD':
                return React.createElement(DiscordIncentive, { incentive: incentive });
            case 'CREATE_N_OR_MORE_PRODUCTS':
                return React.createElement(ProductsIncentive, { incentive: incentive });
            case 'INSTALL_MOBILE_APP':
                return React.createElement(MobileAppIncentive, { incentive: incentive });
            case 'INSTALL_N_OR_MORE_APPS':
                return React.createElement(AppsIncentive, { incentive: incentive });
            case 'CREATE_PROMOTION':
                return React.createElement(PromotionIncentive, { incentive: incentive });
            case 'ORDER_SAMPLES':
                return React.createElement(SamplesIncentive, { incentive: incentive });
            case 'MAKE_FIRST_SALES':
                return React.createElement(MakeYourFirstSalesIncentive, { incentive: incentive });
            case 'DOUBLE_SIGNUP_REFERRAL_BONUS':
                return React.createElement(DoubleReferralBonusIncentive, { incentive: incentive });
        }
    };
    const getSuggestion = () => {
        if (!visible)
            return null;
        if (!campaign.isEnrolled) {
            return (React.createElement(EnrollBanner.Root, null,
                React.createElement(EnrollBanner.Content, null,
                    React.createElement(EnrollBanner.Title, null, campaign?.displayInfo.title),
                    React.createElement(EnrollBanner.Description, null, campaign?.displayInfo.description)),
                React.createElement(EnrollBanner.Actions, null,
                    campaign?.displayInfo.primaryButton?.title && (React.createElement(Button, { label: campaign?.displayInfo.primaryButton?.title, appearance: "semi-transparent", size: "small", onClick: async () => {
                            await enrollCampaignMutation.mutateAsync([{ campaignId: campaign.onboardingCampaignId }], {
                                onSuccess: () => {
                                    campaignQuery.refetch();
                                    Toasts.notify('You have successfully enrolled in the campaign.', {
                                        type: 'success',
                                    });
                                },
                            });
                        }, disabled: enrollCampaignMutation.isLoading })),
                    campaign?.displayInfo.secondaryButton?.title && (React.createElement(Button, { label: campaign?.displayInfo.secondaryButton?.title, href: campaign?.displayInfo.secondaryButton?.uri, target: "_blank", appearance: "semi-transparent", size: "small" })))));
        }
        if (hasFeatureFlag('dashboard_onboarding_campagin_june_2026')) {
            return (React.createElement(Campaign, { title: campaign.displayInfo.title, description: campaign.displayInfo.description, bonusSummary: campaign.bonusSummary, incentives: campaign.campaignIncentives.map(getIncentive).filter(nonNullable), campaignIncentives: campaign.campaignIncentives, onDismiss: createOnDismiss(recommendationsDismiss.campaign.dismiss) }));
        }
        return null;
    };
    const suggestion = getSuggestion();
    return {
        campaignRecommendation: {
            id: 'campaign',
            enabled: Boolean(suggestion),
            isLoading: isLoadingShopStage || campaignQuery.isInitialLoading,
            suggestion,
        },
    };
};
